import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIkigai } from '../lib/stores/ikigai';
import { useValues } from '../lib/stores/values';
import { useLifeAreas } from '../lib/stores/lifeareas';
import { useVision } from '../lib/stores/vision';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';

const FOUNDATION_TOOLS = [
  {
    id: 'ikigai',
    step: 1,
    title: 'Discover Your Ikigai',
    emoji: '🎯',
    description: 'Find your reason for being at the intersection of what you love, what you\'re good at, what the world needs, and what you can be paid for.',
    path: '/purpose/ikigai',
    color: 'rgba(245, 158, 11, 0.1)',
  },
  {
    id: 'values',
    step: 2,
    title: 'Core Values',
    emoji: '💎',
    description: 'Identify and rank your core values to guide your decisions and align your life with what matters most to you.',
    path: '/purpose/values',
    color: 'rgba(59, 130, 246, 0.1)',
  },
  {
    id: 'wheel',
    step: 3,
    title: 'Life Wheel',
    emoji: '⭕',
    description: 'Rate different areas of your life to visualize balance and identify where to focus your energy for growth.',
    path: '/purpose/wheel',
    color: 'rgba(34, 197, 94, 0.1)',
  },
  {
    id: 'vision',
    step: 4,
    title: 'Vision Statements',
    emoji: '🔮',
    description: 'Create compelling vision statements for different life areas to clarify your ideal future and inspire action.',
    path: '/purpose/vision',
    color: 'rgba(168, 85, 247, 0.1)',
  },
];

export default function FoundationPage() {
  const navigate = useNavigate();

  // Check completion status
  const { statement: ikigaiStatement, initialized: ikigaiInit, initialize: initIkigai } = useIkigai();
  const { values, initialized: valuesInit, initialize: initValues } = useValues();
  const { areas, initialized: areasInit, initialize: initAreas } = useLifeAreas();
  const { visions, initialized: visionsInit, initialize: initVisions } = useVision();

  useEffect(() => {
    if (!ikigaiInit) initIkigai();
    if (!valuesInit) initValues();
    if (!areasInit) initAreas();
    if (!visionsInit) initVisions();
  }, [ikigaiInit, valuesInit, areasInit, visionsInit]);

  const getCompletionStatus = (id: string) => {
    switch (id) {
      case 'ikigai':
        return !!ikigaiStatement;
      case 'values':
        return values.length >= 3;
      case 'wheel':
        return areas.some(a => a.latest_rating !== null);
      case 'vision':
        return visions.length > 0;
      default:
        return false;
    }
  };

  const getNextIncomplete = () => {
    return FOUNDATION_TOOLS.find(tool => !getCompletionStatus(tool.id));
  };

  const nextTool = getNextIncomplete();
  const completedCount = FOUNDATION_TOOLS.filter(tool => getCompletionStatus(tool.id)).length;

  return (
    <div className="page-container">
      <div style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)', textAlign: 'left' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
          fontWeight: 400,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.5rem',
        }}>
          Foundation Journey
        </h1>
        <p style={{ color: 'var(--color-neutral-600)', fontSize: 'clamp(1rem, 2vw, 1.125rem)', marginBottom: '1rem' }}>
          Complete these four tools in sequence to build a strong foundation for your life
        </p>

        {/* Progress Bar */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
              Progress: {completedCount} of 4 complete
            </span>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
              {Math.round((completedCount / 4) * 100)}%
            </span>
          </div>
          <div style={{
            height: '8px',
            backgroundColor: 'var(--color-neutral-200)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${(completedCount / 4) * 100}%`,
              backgroundColor: 'var(--color-primary-600)',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>

        {/* Next Step Button */}
        {nextTool && (
          <div style={{ marginTop: '1.5rem' }}>
            <Button onClick={() => navigate(nextTool.path)}>
              Continue Journey: {nextTool.title} →
            </Button>
          </div>
        )}

        {completedCount === 4 && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: 'var(--color-success-50)',
            border: '2px solid var(--color-success-200)',
            borderRadius: '12px',
            marginTop: '1.5rem',
          }}>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--color-success-900)',
              marginBottom: '0.5rem',
            }}>
              🎉 Foundation Complete!
            </h3>
            <p style={{ color: 'var(--color-success-800)', lineHeight: '1.6' }}>
              You've completed all four foundation tools. Your purpose, values, life balance, and vision are now clearly defined.
              You can revisit any tool below or move on to setting goals.
            </p>
          </div>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: 'clamp(1rem, 2vw, 2rem)',
        marginTop: 'clamp(1.5rem, 3vw, 2rem)',
      }}>
        {FOUNDATION_TOOLS.map(tool => {
          const isComplete = getCompletionStatus(tool.id);
          return (
            <div
              key={tool.id}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
              }}
              onClick={(e) => {
                e.preventDefault();
                navigate(tool.path);
              }}
            >
              <Card
                style={{
                  padding: 'clamp(1.5rem, 3vw, 2.5rem)',
                  backgroundColor: tool.color,
                  border: isComplete ? '2px solid var(--color-success-400)' : '2px solid transparent',
                  position: 'relative',
                  height: '100%',
                }}
              >
                {/* Step Number Badge */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: isComplete ? 'var(--color-success-500)' : 'var(--color-neutral-300)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                }}>
                  {isComplete ? '✓' : tool.step}
                </div>

                <div style={{ textAlign: 'center', marginBottom: '1.5rem', marginTop: '1rem' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                    {tool.emoji}
                  </div>
                  <h2 style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: 'var(--color-neutral-900)',
                    marginBottom: '0.75rem',
                  }}>
                    {tool.title}
                  </h2>
                </div>
                <p style={{
                  color: 'var(--color-neutral-700)',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  textAlign: 'center',
                  marginBottom: '2rem',
                }}>
                  {tool.description}
                </p>
                <div style={{
                  textAlign: 'center',
                  color: isComplete ? 'var(--color-success-600)' : 'var(--color-primary-600)',
                  fontWeight: 600,
                }}>
                  {isComplete ? '✓ Completed - Review' : 'Start →'}
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: '3rem',
        padding: '2rem',
        backgroundColor: 'var(--color-primary-50)',
        borderRadius: '12px',
        border: '1px solid var(--color-primary-200)',
      }}>
        <h3 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.75rem',
        }}>
          💡 The Foundation Journey
        </h3>
        <p style={{
          color: 'var(--color-neutral-700)',
          lineHeight: '1.6',
          marginBottom: '1rem',
        }}>
          These four tools build on each other to create a complete picture of who you are and where you're going:
        </p>
        <ol style={{
          color: 'var(--color-neutral-700)',
          lineHeight: '1.8',
          paddingLeft: '1.5rem',
        }}>
          <li><strong>Ikigai:</strong> Discover your core purpose and reason for being</li>
          <li><strong>Values:</strong> Define what matters most to guide your decisions</li>
          <li><strong>Life Wheel:</strong> Assess balance across all life areas</li>
          <li><strong>Vision:</strong> Paint a clear picture of your ideal future</li>
        </ol>
        <p style={{
          color: 'var(--color-neutral-700)',
          lineHeight: '1.6',
          marginTop: '1rem',
        }}>
          Complete them in sequence for the best experience, but you can revisit any tool at any time.
        </p>
      </div>
    </div>
  );
}
